import {
  AuctionProposalStatus,
  NegotiationSubStep,
} from "@/models/credit-auction-proposal.model";
import {
  ProposalCustomStatus,
  ProposalCustomStatusItenStatus,
} from "@/models/consignado/proposal.model";

// Ordem dos passos exibidos na coluna de progresso e oferecidos no filtro
// "Progresso Contratação". CPF_INCLUSION fica de fora: o passo `cpf` nasce já
// como `sucesso` (é confirmado na landing, primeira interação), então nunca há
// proposta "aguardando CPF" — a confirmação do CPF é absorvida no passo de
// WhatsApp (ver label).
export const NEGOTIATION_SUB_STEP_ORDER: NegotiationSubStep[] = [
  NegotiationSubStep.WHATSAPP_CONFIRMATION,
  NegotiationSubStep.SIMULATION,
  NegotiationSubStep.PERSONAL_DATA,
  NegotiationSubStep.ADDRESS,
  NegotiationSubStep.BANK_DATA,
  NegotiationSubStep.ANALISE,
  NegotiationSubStep.PROPOSTA,
  NegotiationSubStep.REVISAO,
  NegotiationSubStep.CONSENTIMENTO,
  NegotiationSubStep.LOCALIZATION,
  NegotiationSubStep.ACEITE_CONTRATACAO,
  NegotiationSubStep.BIOMETRIA,
  NegotiationSubStep.FORMALIZATION,
];

export const NEGOTIATION_SUB_STEP_LABELS: Record<NegotiationSubStep, string> = {
  [NegotiationSubStep.CPF_INCLUSION]: "Inclusão de CPF",
  [NegotiationSubStep.WHATSAPP_CONFIRMATION]: "Confirmação de CPF e WhatsApp",
  [NegotiationSubStep.SIMULATION]: "Simulação",
  [NegotiationSubStep.PERSONAL_DATA]: "Dados pessoais",
  [NegotiationSubStep.ADDRESS]: "Endereço",
  [NegotiationSubStep.BANK_DATA]: "Dados bancários",
  [NegotiationSubStep.ANALISE]: "Análise",
  [NegotiationSubStep.PROPOSTA]: "Proposta",
  [NegotiationSubStep.REVISAO]: "Revisão",
  [NegotiationSubStep.CONSENTIMENTO]: "Consentimento",
  [NegotiationSubStep.LOCALIZATION]: "Localização",
  [NegotiationSubStep.ACEITE_CONTRATACAO]: "Aceite da contratação",
  [NegotiationSubStep.BIOMETRIA]: "Biometria",
  [NegotiationSubStep.FORMALIZATION]: "Formalização",
};

export const AUCTION_PROPOSAL_STATUS_LABELS: Record<string, string> = {
  [AuctionProposalStatus.CREATED]: "Proposta ofertada BMP",
  [AuctionProposalStatus.CHOSEN]: "Leilão vencido BMP",
  [AuctionProposalStatus.PENDING_CONFIRMATION]: "Início Contratação",
  [AuctionProposalStatus.PENDING_FORMALIZATION]: "Aguard. Formalização",
  [AuctionProposalStatus.PENDING_REFORMALIZATION]: "Aguard. Reformalização",
  [AuctionProposalStatus.PENDING_USER_REVIEW]: "Aguard. Revisão",
  [AuctionProposalStatus.PAYED]: "Pago",
  [AuctionProposalStatus.IN_CANCELLATION]: "Em Cancelamento",
  [AuctionProposalStatus.CANCELED]: "Cancelado",
  [AuctionProposalStatus.ERROR_CREATE_BIOMETRIC_VALIDATION]: "Erro Biométrico",
  [AuctionProposalStatus.REPROVED]: "Leilão perdido",
  [AuctionProposalStatus.REPROVED_IN_PROCESS]: "Reprovado no processamento",
  [AuctionProposalStatus.PENDING_BANK_ANALYSIS]: "Aguard. Análise Bancária",
  [AuctionProposalStatus.CANCELED_BY_CLIENT]: "Cancelado pelo Cliente",
  [AuctionProposalStatus.SELFSERVICE]: "Self-Service",
  [AuctionProposalStatus.IN_AUCTION]: "Em Leilão",
  [AuctionProposalStatus.PROCESSING_SIMULATION]: "Processando simulação",
  [AuctionProposalStatus.AWAITING_BORROWER_CHOICE]:
    "Aguardando escolha do cliente",
};

// Ordem dos sub-steps gravados no JSON `statusCustomizado.autocontratacao`
// pelo consignado-trabalhador (vide `getDefaultAutocontratacaoStatus`).
// Alinhada ao funil do redesign (rotas do autocontratacao-ssr).
const AUTOCONTRATACAO_FUNNEL: ReadonlyArray<{
  key: keyof NonNullable<ProposalCustomStatus["autocontratacao"]>;
  subStep: NegotiationSubStep;
}> = [
  { key: "cpf", subStep: NegotiationSubStep.CPF_INCLUSION },
  { key: "whatsapp", subStep: NegotiationSubStep.WHATSAPP_CONFIRMATION },
  { key: "simulacao", subStep: NegotiationSubStep.SIMULATION },
  { key: "dados_pessoais", subStep: NegotiationSubStep.PERSONAL_DATA },
  { key: "endereco", subStep: NegotiationSubStep.ADDRESS },
  { key: "dados_bancarios", subStep: NegotiationSubStep.BANK_DATA },
  { key: "analise", subStep: NegotiationSubStep.ANALISE },
  { key: "proposta", subStep: NegotiationSubStep.PROPOSTA },
  { key: "revisao", subStep: NegotiationSubStep.REVISAO },
  { key: "consentimento", subStep: NegotiationSubStep.CONSENTIMENTO },
  { key: "localizacao", subStep: NegotiationSubStep.LOCALIZATION },
  { key: "aceite_contratacao", subStep: NegotiationSubStep.ACEITE_CONTRATACAO },
  { key: "biometria", subStep: NegotiationSubStep.BIOMETRIA },
];

// Deriva o sub-step "atual" do funil de autocontratação a partir do JSON
// `statusCustomizado` da proposta. Convenção: o sub-step atual é o primeiro
// item da ordem que ainda NÃO está como SUCESSO. Se todos já foram
// concluídos, devolve o último (BIOMETRIA) — daí em diante a fase
// passa para PENDING_FORMALIZATION e o componente StatusSteps marca tudo
// como completed.
//
// Retorna `null` quando não há `autocontratacao` no JSON (proposta antiga
// ou ainda sem nenhum step marcado), o que faz o StatusSteps cair no
// CPF_INCLUSION como current — comportamento esperado.
export function deriveNegotiationSubStep(
  statusCustomizado: string | null | undefined,
): NegotiationSubStep | null {
  if (!statusCustomizado) return null;

  let parsed: Partial<ProposalCustomStatus> | null = null;
  try {
    parsed = JSON.parse(statusCustomizado) as Partial<ProposalCustomStatus>;
  } catch {
    return null;
  }

  const autoc = parsed?.autocontratacao;
  if (!autoc) return null;

  for (const { key, subStep } of AUTOCONTRATACAO_FUNNEL) {
    const item = autoc[key];
    if (item?.status !== ProposalCustomStatusItenStatus.SUCESSO) {
      return subStep;
    }
  }
  return NegotiationSubStep.BIOMETRIA;
}

// Lê `statusCustomizado.leilao.tipo_taxa.mensagem` (ex.:
// "24/09/26 10:53: tipo de taxa =Reduzida") e devolve "Reduzida" quando a
// taxa do lance foi a reduzida. Qualquer outro caso (sem leilão, JSON
// inválido, outro tipo de taxa) devolve `null`.
export function getAuctionTaxType(
  statusCustomizado: string | null | undefined,
): "Reduzida" | null {
  if (!statusCustomizado) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(statusCustomizado);
    // O valor pode chegar serializado duas vezes (string JSON dentro de string).
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
  } catch {
    return null;
  }

  const mensagem = (
    parsed as { leilao?: { tipo_taxa?: { mensagem?: string } } } | null
  )?.leilao?.tipo_taxa?.mensagem;

  return mensagem?.includes("Reduzida") ? "Reduzida" : null;
}

export const AUCTION_PROPOSAL_STATUS_COLORS: Record<string, string> = {
  [AuctionProposalStatus.CREATED]: "blue",
  [AuctionProposalStatus.CHOSEN]: "cyan",
  [AuctionProposalStatus.PENDING_CONFIRMATION]: "orange",
  [AuctionProposalStatus.PENDING_FORMALIZATION]: "orange",
  [AuctionProposalStatus.PENDING_REFORMALIZATION]: "yellow",
  [AuctionProposalStatus.PENDING_USER_REVIEW]: "yellow",
  [AuctionProposalStatus.PAYED]: "green",
  [AuctionProposalStatus.IN_CANCELLATION]: "red",
  [AuctionProposalStatus.CANCELED]: "red",
  [AuctionProposalStatus.ERROR_CREATE_BIOMETRIC_VALIDATION]: "red",
  [AuctionProposalStatus.REPROVED]: "red",
  [AuctionProposalStatus.REPROVED_IN_PROCESS]: "red",
  [AuctionProposalStatus.PENDING_BANK_ANALYSIS]: "orange",
  [AuctionProposalStatus.CANCELED_BY_CLIENT]: "red",
  [AuctionProposalStatus.SELFSERVICE]: "teal",
  [AuctionProposalStatus.IN_AUCTION]: "purple",
  [AuctionProposalStatus.PROCESSING_SIMULATION]: "orange",
  [AuctionProposalStatus.AWAITING_BORROWER_CHOICE]: "yellow",
};
